import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const formatPrice = (price) => {
  const value = parseFloat(price);
  return `$${(Number.isNaN(value) ? 0 : value).toFixed(2)}`;
};

const isDraftNonRxItem = (item) =>
  !!(item && (item.isDraft || String(item.id || '').startsWith('nonrx-')));

const NonRxCart = ({
  t,
  items,
  disabled,
  allowAdd,
  priceEditable,
  editingPriceIndex,
  onAddItem,
  onChangeItem,
  onRemoveItem,
  onStartPriceEdit,
  onSavePriceEdit,
  onSaveDraftItem,
}) => {
  const rowData = Array.isArray(items) ? items : [];
  const isEmpty = rowData.length === 0;
  const canAdd = allowAdd !== false;
  const headerCellSx = {
    fontSize: '0.68rem',
    fontWeight: 600,
    whiteSpace: 'normal',
    lineHeight: 1.2,
    verticalAlign: 'bottom',
    padding: '6px 4px',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    color: '#0369a1',
    backgroundColor: '#e0f2fe',
  };

  return (
    <Box className="assign-non-rx-cart">
      <Box className="assign-non-rx-cart-toolbar">
        <Typography className="assign-non-rx-cart-title" variant="subtitle2">
          {t('Assign.NonRxCart.Title', { defaultValue: 'Non-Rx Items' })}
        </Typography>
        <Button
          size="small"
          color="primary"
          variant="outlined"
          startIcon={<AddCircleOutlineRounded />}
          onClick={onAddItem}
          disabled={!canAdd}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            borderWidth: 1.5,
            px: 1.5,
            '&:disabled': {
              borderWidth: 1.5,
            },
          }}
        >
          {t('Assign.NonRxCart.AddItem', { defaultValue: 'Add item' })}
        </Button>
      </Box>
      <div
        className={
          'assign-rx-cart-scroll assign-non-rx-cart-scroll' +
          (isEmpty ? ' is-empty' : '')
        }
      >
        <Table size="small" aria-label="non-rx cart" className="assign-non-rx-table">
          <TableHead>
            <TableRow style={{ height: 'auto' }}>
              <TableCell padding="none" sx={headerCellSx}>
                {`${t('Assign.NonRxCart.ItemNumber', {
                  defaultValue: 'Item #',
                })} (${rowData.length})`}
              </TableCell>
              <TableCell padding="none" sx={headerCellSx}>
                {t('Assign.NonRxCart.Description', {
                  defaultValue: 'Description',
                })}
              </TableCell>
              <TableCell padding="none" sx={{ ...headerCellSx, width: 140 }}>
                {t('Assign.NonRxCart.Price', { defaultValue: 'Price' })}
              </TableCell>
              <TableCell padding="none" sx={{ ...headerCellSx, width: 90 }}>
                {t('Assign.NonRxCart.Qty', { defaultValue: 'Qty' })}
              </TableCell>
              <TableCell
                padding="none"
                sx={{ ...headerCellSx, width: 88, maxWidth: 88 }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty ? (
              <TableRow>
                <TableCell colSpan={5} className="recent-orders-empty-cell">
                  {t('Assign.NonRxCart.Empty', {
                    defaultValue: 'No Non-Rx items added',
                  })}
                </TableCell>
              </TableRow>
            ) : (
              rowData.map((item, index) => {
                const isDraft = isDraftNonRxItem(item);
                const rowLocked = disabled && !isDraft;
                const isEditingPrice =
                  !isDraft && priceEditable && editingPriceIndex === index;
                const showAssignedPriceControls =
                  rowLocked && priceEditable;

                return (
                  <TableRow key={item.id || index}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={item.item_number || ''}
                        disabled={rowLocked}
                        onChange={(event) =>
                          onChangeItem(index, 'item_number', event.target.value)
                        }
                        inputProps={{ maxLength: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        required
                        value={item.description || ''}
                        disabled={rowLocked}
                        onChange={(event) =>
                          onChangeItem(index, 'description', event.target.value)
                        }
                        inputProps={{ maxLength: 255 }}
                      />
                    </TableCell>
                    <TableCell className="assign-rx-cart-price-cell-wrap">
                      {showAssignedPriceControls ? (
                        isEditingPrice ? (
                          <Box className="assign-rx-cart-price-cell">
                            <TextField
                              size="small"
                              variant="standard"
                              type="number"
                              value={
                                item.price === 0 || item.price ? item.price : ''
                              }
                              onChange={(event) =>
                                onChangeItem(index, 'price', event.target.value)
                              }
                              inputProps={{ min: 0, step: '0.01' }}
                              sx={{ width: 72 }}
                            />
                            <Tooltip
                              title={t('Assign.SaveTooltip', {
                                defaultValue: 'Save',
                              })}
                            >
                              <IconButton
                                size="small"
                                onClick={() => onSavePriceEdit(index)}
                                aria-label={t('Assign.SaveTooltip', {
                                  defaultValue: 'Save',
                                })}
                              >
                                <SaveIcon style={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box className="assign-rx-cart-price-cell">
                            <span className="assign-rx-cart-price-value">
                              {formatPrice(item.price)}
                            </span>
                            <Tooltip
                              title={t('Assign.EditTooltip', {
                                defaultValue: 'Edit',
                              })}
                            >
                              <IconButton
                                size="small"
                                onClick={() => onStartPriceEdit(index)}
                                aria-label={t('Assign.EditTooltip', {
                                  defaultValue: 'Edit',
                                })}
                              >
                                <EditIcon style={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          variant="standard"
                          type="number"
                          value={item.price === 0 || item.price ? item.price : ''}
                          disabled={rowLocked && !priceEditable}
                          onChange={(event) =>
                            onChangeItem(index, 'price', event.target.value)
                          }
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        type="number"
                        value={item.qty === 0 || item.qty ? item.qty : ''}
                        disabled={rowLocked}
                        onChange={(event) =>
                          onChangeItem(index, 'qty', event.target.value)
                        }
                        inputProps={{ min: 1, step: '1' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isDraft && disabled && (
                          <Tooltip
                            title={t('Assign.NonRxCart.SaveToOrder', {
                              defaultValue: 'Save to order',
                            })}
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onSaveDraftItem(index)}
                              aria-label={t('Assign.NonRxCart.SaveToOrder', {
                                defaultValue: 'Save to order',
                              })}
                            >
                              <SaveIcon style={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          aria-label={t('Assign.NonRxCart.Remove', {
                            defaultValue: 'Remove',
                          })}
                          onClick={() => onRemoveItem(index)}
                          disabled={rowLocked}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Box>
  );
};

NonRxCart.propTypes = {
  t: PropTypes.func.isRequired,
  items: PropTypes.array,
  disabled: PropTypes.bool,
  allowAdd: PropTypes.bool,
  priceEditable: PropTypes.bool,
  editingPriceIndex: PropTypes.number,
  onAddItem: PropTypes.func.isRequired,
  onChangeItem: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onStartPriceEdit: PropTypes.func,
  onSavePriceEdit: PropTypes.func,
  onSaveDraftItem: PropTypes.func,
};

NonRxCart.defaultProps = {
  items: [],
  disabled: false,
  allowAdd: true,
  priceEditable: false,
  editingPriceIndex: null,
  onStartPriceEdit: () => {},
  onSavePriceEdit: () => {},
  onSaveDraftItem: () => {},
};

export default NonRxCart;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/NonRxCart.jsx